import { BrowserBackLink } from "@/components/BrowserBackLink";
import { useBreadcrumb } from "@/contexts/BreadcrumbContext";
import { useEffect } from "react";

export function Privacy() {
  const { clearBreadcrumbs } = useBreadcrumb();

  useEffect(() => {
    clearBreadcrumbs();
  }, []);

  return (
    <div className="container mx-auto p-8 max-w-3xl min-h-screen">
      <BrowserBackLink className="text-sm text-muted-foreground hover:text-foreground mb-8 inline-block" />
      <h1 className="text-3xl font-bold mb-2">Privacy Policy</h1>
      <p className="text-muted-foreground mb-8">Effective Date: March 17, 2026</p>

      <div className="prose prose-slate dark:prose-invert max-w-none space-y-6">
        <section>
          <h2 className="text-xl font-semibold mt-8 mb-4">1. Introduction</h2>
          <p>
            This Privacy Policy describes how XYZ Digital, Inc. dba Engram ("Engram", "we", "us" or
            "our") policies and procedures on the collection, use and disclosure of Your information
            when You use the Service and tells You about Your privacy rights and how the law
            protects You.
          </p>
          <p>
            We use Your Personal data to provide and improve the Service. By using the Service, You
            agree to the collection and use of information in accordance with this Privacy Policy.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold mt-8 mb-4">2. Definitions</h2>
          <ul className="list-disc pl-6 space-y-2">
            <li>
              <strong>SERVICE</strong> means operated by Engram.
            </li>
            <li>
              <strong>PERSONAL DATA</strong> means data about a living individual who can be
              identified from those data.
            </li>
            <li>
              <strong>USAGE DATA</strong> is data collected automatically either generated by the
              use of Service or from Service infrastructure itself (for example, the duration of a
              page visit).
            </li>
            <li>
              <strong>COOKIES</strong> are small files stored on your device (computer or mobile
              device).
            </li>
            <li>
              <strong>THE USER</strong> is the individual using our Service. The User corresponds
              to the Data Subject, who is the subject of Personal Data.
            </li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-semibold mt-8 mb-4">3. Information We Collect</h2>
          <h3 className="text-lg font-medium mt-6 mb-2">Personal Data</h3>
          <p>
            While using Our Service, We may ask You to provide Us with certain personally
            identifiable information that can be used to contact or identify You. Personally
            identifiable information may include, but is not limited to:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>
              Contact data, such as first and last name, email address, mailing address, and phone
              number.
            </li>
            <li>
              Profile data, such as your username and password that you use to log in to the
              Service, social network handles, biographical details, photographs, links, and
              descriptions that you add to your profile.
            </li>
            <li>
              Communications that we exchange with you, such as emails, chat messages, and other
              communications that you exchange with others through the Service.
            </li>
            <li>
              Payments and transactional data needed to complete your orders, support payments, or
              other payments on or through the service (such as payment card information, billing
              and shipping information, payment processor information, financial account
              information).
            </li>
            <li>
              Marketing data, such as your preferences and settings for receiving marketing
              communications from us and our third parties, and information you provide to us when
              you interact with our advertising and applications on third-party websites and
              services.
            </li>
            <li>Product data, such as the description of a product or digital good for sale and its price.</li>
          </ul>

          <h3 className="text-lg font-medium mt-6 mb-2">Non-Personal Data</h3>
          <p>
            While using our Service, we may ask you to provide us with certain Non-Personal Data.
            "Non-Personal Data" is any information not relating to an identified or identifiable
            natural person. Non-Personal Data we may collect includes:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Usage Data: explained below.</li>
            <li>Cookies and Tracking Technologies: explained below.</li>
            <li>
              Aggregate Information: data about how you use the Service, such as the types of
              content you view or engage with, the features you use, the actions you take, and the
              devices you use to access the Service in order to help us better develop new features
              and tailor the Service.
            </li>
          </ul>

          <h3 className="text-lg font-medium mt-6 mb-2">Usage Data</h3>
          <p>Usage Data is collected automatically when using the Service.</p>
          <p>
            Usage Data may include information such as Your Device's Internet Protocol address
            (e.g. IP address), browser type, browser version, the pages of our Service that You
            visit, the time and date of Your visit, the time spent on those pages, unique device
            identifiers and other diagnostic data.
          </p>
          <p>
            When You access the Service by or through a mobile device, We may collect certain
            information automatically, including, but not limited to, the type of mobile device You
            use, Your mobile device unique ID, the IP address of Your mobile device, Your mobile
            operating system, the type of mobile Internet browser You use, unique device identifiers
            and other diagnostic data.
          </p>
          <p>
            We may also collect information that Your browser sends whenever You visit our Service
            or when You access the Service by or through a mobile device.
          </p>

          <h3 className="text-lg font-medium mt-6 mb-2">Information from Third-Party Social Media Services</h3>
          <p>
            The Company allows You to create an account and log in to use the Service through the
            following Third-party Social Media Services:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Google</li>
            <li>Instagram</li>
          </ul>
          <p>
            If You decide to register through or otherwise grant us access to a Third-Party Social
            Media Service, We may collect Personal data that is already associated with Your
            Third-Party Social Media Service's account, such as Your email address or profile
            picture.
          </p>

          <h3 className="text-lg font-medium mt-6 mb-2">Cookies and Tracking Technologies</h3>
          <p>
            Cookies are files with a small amount of data which may include an anonymous unique
            identifier. Cookies are sent to your browser from a website and stored on your device.
            The data is sent back to the website that originated the cookie. This Website uses
            cookies to improve your experience and to provide you with a better service. Cookies
            used on our sites include both "session cookies" that are deleted when a session ends,
            "persistent cookies" that remain longer, "first party" cookies that we place and "third
            party" cookies that our third-party business partners and service providers place.
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>
              Local storage technologies, such as HTML5, are used to store non-personal data, such
              as your preferences and settings.
            </li>
            <li>
              Web beacons, also known as pixel tags or clear GIFs, which are used to demonstrate
              that a webpage or email was accessed or opened, or that certain content was viewed or
              clicked.
            </li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-semibold mt-8 mb-4">4. Use of Data</h2>
          <p>Engram uses the collected data for the following purposes:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>to provide and maintain our Service;</li>
            <li>to notify you about changes to our Service;</li>
            <li>to allow you to participate in interactive features of our Service when you choose to do so;</li>
            <li>to provide customer support;</li>
            <li>to gather analysis or valuable information so that we can improve the Service;</li>
            <li>to monitor the usage of the Service;</li>
            <li>to detect, prevent and address technical issues;</li>
            <li>to fulfill any other purpose for which you provide it;</li>
            <li>to carry out our obligations and enforce our rights arising from any contracts entered into between you and us;</li>
            <li>to provide you with notices about your account, including expiration and renewal notices;</li>
            <li>
              to provide you with news, special offers and general information about other goods,
              services and events which we offer that are similar to those that you have already
              purchased or enquired about unless you have opted not to receive such information;
            </li>
            <li>in any other way we may describe when you provide the information;</li>
            <li>for any other purpose with your consent.</li>
          </ul>
          <p>
            No personal information (e.g. name, email, address, phone number, etc.) will be shared
            with third parties/affiliates for marketing/promotional purposes.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold mt-8 mb-4">5. Data Retention</h2>
          <p>
            We will retain Your Personal Data only for as long as is necessary for the purposes set
            out in this Privacy Policy. We will retain and use Your Personal Data to the extent
            necessary to comply with our legal obligations (for example, if we are required to
            retain your data to comply with applicable laws), resolve disputes, and enforce our
            legal agreements and policies.
          </p>
          <p>
            The Company will also retain Usage Data for internal analysis purposes. Usage Data is
            generally retained for a shorter period of time, except when this data is used to
            strengthen the security or to improve the functionality of Our Service, or We are
            legally obligated to retain this data for longer time periods.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold mt-8 mb-4">6. Transfer of Your Personal Data</h2>
          <p>
            Your information, including Personal Data, is processed at the Company's operating
            offices and in any other places where the parties involved in the processing are
            located. It means that this information may be transferred to — and maintained on —
            computers located outside of Your state, province, country or other governmental
            jurisdiction where the data protection laws may differ from those from Your
            jurisdiction.
          </p>
          <p>
            Your consent to this Privacy Policy followed by Your submission of such information
            represents Your agreement to that transfer.
          </p>
          <p>
            The Company will take all steps reasonably necessary to ensure that Your data is
            treated securely and in accordance with this Privacy Policy and no transfer of Your
            Personal Data will take place to an organization or a country unless there are adequate
            controls in place including the security of Your data and other personal information.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold mt-8 mb-4">7. Delete Your Personal Data</h2>
          <p>
            You have the right to delete or request that We assist in deleting the Personal Data
            that We have collected about You.
          </p>
          <p>
            Our Service may give You the ability to delete certain information about You from within
            the Service.
          </p>
          <p>
            You may update, amend, or delete Your information at any time by signing in to Your
            Account, if you have one, and visiting the account settings section that allows you to
            manage Your personal information. You may also contact Us to request access to, correct,
            or delete any personal information that You have provided to Us.
          </p>
          <p>
            Please note, however, that We may need to retain certain information when we have a
            legal obligation or lawful basis to do so.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold mt-8 mb-4">8. Disclosure of Your Personal Data</h2>
          <h3 className="text-lg font-medium mt-6 mb-2">Business Transactions</h3>
          <p>
            If the Company is involved in a merger, acquisition or asset sale, Your Personal Data
            may be transferred. We will provide notice before Your Personal Data is transferred and
            becomes subject to a different Privacy Policy.
          </p>
          <h3 className="text-lg font-medium mt-6 mb-2">Law enforcement</h3>
          <p>
            Under certain circumstances, the Company may be required to disclose Your Personal Data
            if required to do so by law or in response to valid requests by public authorities
            (e.g. a court or a government agency).
          </p>
          <h3 className="text-lg font-medium mt-6 mb-2">Other legal requirements</h3>
          <p>
            The Company may disclose Your Personal Data in the good faith belief that such action is
            necessary to:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Comply with a legal obligation</li>
            <li>Protect and defend the rights or property of the Company</li>
            <li>Prevent or investigate possible wrongdoing in connection with the Service</li>
            <li>Protect the personal safety of Users of the Service or the public</li>
            <li>Protect against legal liability</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-semibold mt-8 mb-4">9. Security of Your Personal Data</h2>
          <p>
            The security of Your Personal Data is important to Us, but remember that no method of
            transmission over the Internet, or method of electronic storage is 100% secure. While
            We strive to use commercially acceptable means to protect Your Personal Data, We cannot
            guarantee its absolute security.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold mt-8 mb-4">10. Children's Privacy</h2>
          <p>
            Our Service does not address anyone under the age of 13. We do not knowingly collect
            personally identifiable information from anyone under the age of 13. If You are a parent
            or guardian and You are aware that Your child has provided Us with Personal Data,
            please contact Us. If We become aware that We have collected Personal Data from anyone
            under the age of 13 without verification of parental consent, We take steps to remove
            that information from Our servers.
          </p>
          <p>
            If We need to rely on consent as a legal basis for processing Your information and Your
            country requires consent from a parent, We may require Your parent's consent before We
            collect and use that information.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold mt-8 mb-4">11. Links to Other Websites</h2>
          <p>
            Our Service may contain links to other websites that are not operated by Us. If You
            click on a third party link, You will be directed to that third party's site. We
            strongly advise You to review the Privacy Policy of every site You visit.
          </p>
          <p>
            We have no control over and assume no responsibility for the content, privacy policies
            or practices of any third party sites or services.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold mt-8 mb-4">12. Changes to this Privacy Policy</h2>
          <p>
            We may update Our Privacy Policy from time to time. We will notify You of any changes by
            posting the new Privacy Policy on this page.
          </p>
          <p>
            We will let You know via email and/or a prominent notice on Our Service, prior to the
            change becoming effective and update the "Last updated" date at the top of this Privacy
            Policy.
          </p>
          <p>
            You are advised to review this Privacy Policy periodically for any changes. Changes to
            this Privacy Policy are effective when they are posted on this page.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold mt-8 mb-4">Contact Us</h2>
          <p>
            If you have any questions about this Privacy Policy, You can contact us:
          </p>
          <p>
            By email:{" "}
            <a href="mailto:adam@xyzdigital.com" className="text-primary hover:underline">
              adam@xyzdigital.com
            </a>
          </p>
        </section>
      </div>
    </div>
  );
}
